import type { CheckoutOrder, PaymentMethod } from "@/api/types";

/** An online way to pay, as the payment page offers it (suite ADR-0102). */
export interface OnlineMethod {
  value: PaymentMethod;
  label: string;
  how: string;
}

/** The online ways to pay, in the order they're offered. Bank transfer isn't one: it's a pro-forma, not a page. */
export const ONLINE_METHODS: OnlineMethod[] = [
  {
    value: "ESEWA",
    label: "eSewa",
    how: "You approve it on eSewa's page with your eSewa ID and password, then come back here.",
  },
  {
    value: "KHALTI",
    label: "Khalti",
    how: "You approve it on Khalti's page, from your Khalti wallet, mobile banking or a card, then come back here.",
  },
  {
    value: "CONNECT_IPS",
    label: "connectIPS",
    how: "You sign in to connectIPS and pay from your linked bank account, then come back here.",
  },
  {
    value: "CARD",
    label: "Card",
    how: "You pay by card on the secure payment page. Rigo Accounting never sees your card.",
  },
];

/** The online methods of a price book's list that can be paid now, in the order offered. */
export function onlineMethods(methods: readonly string[] | null | undefined): OnlineMethod[] {
  const offered = new Set(methods ?? []);
  return ONLINE_METHODS.filter((m) => offered.has(m.value));
}

/** A method's name, as people say it. */
export function methodLabel(method: string): string {
  return ONLINE_METHODS.find((m) => m.value === method)?.label ?? method;
}

/**
 * Sends the browser to the gateway's page: a form posted with the signed fields RigoSub returned (eSewa, connectIPS),
 * or a plain navigation (Khalti). The fields go as they came; changing one breaks its signature.
 */
export function goToGateway(payment: CheckoutOrder["payment"], doc: Document = document): void {
  if (!payment.redirectUrl) return;
  if (!payment.form) {
    doc.defaultView?.location.assign(payment.redirectUrl);
    return;
  }
  const form = doc.createElement("form");
  form.method = "POST";
  form.action = payment.redirectUrl;
  form.style.display = "none";
  for (const [name, value] of Object.entries(payment.form)) {
    const input = doc.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  doc.body.appendChild(form);
  form.submit();
}
